import * as React from 'react'

import { clsx } from 'clsx'
import { animate, useMotionValue, useMotionValueEvent } from 'motion/react'

import { Flex, Heading, Progress } from '@buffer-mono/popcorn'

import type { CommentsScoreMetric } from '../../hooks/scoring/useCommentsScore'

import { getScoreColors } from '../../util/commentScoreTheme'
import { EASE_IN_OUT } from '../../util/easingRules'

import styles from './DetailedMetrics.module.css'

import type { JSX } from 'react'

interface ProgressCssProperties extends React.CSSProperties {
  '--progress-fg-color'?: string
  '--progress-value'?: string
  '--progress-max'?: string
}
const Metric = ({
  label,
  value,
}: {
  label: string
  value: number
}): JSX.Element => {
  const colors = getScoreColors(value)
  const progress = useMotionValue(0)
  const [currentValue, setCurrentValue] = React.useState(0)

  const maxDuration = 2 // seconds for 100
  const minDuration = 0.5 // minimum duration for very low values
  const duration = Math.max((value / 100) * maxDuration, minDuration)

  // Subscribe to motion value changes to update UI
  useMotionValueEvent(progress, 'change', (latest) => {
    setCurrentValue(latest)
  })

  React.useEffect(() => {
    const controls = animate(progress, value, {
      duration,
      ease: EASE_IN_OUT.cubic,
    })
    return (): void => {
      controls.stop()
    }
  }, [progress, value, duration])

  const progressStyle: ProgressCssProperties = {
    '--progress-fg-color': colors.progressColor,
    '--progress-value': currentValue.toString(),
    '--progress-max': '100',
  }

  return (
    <Flex direction="column" gap="2xs" style={{ width: '100%' }}>
      <Flex justify="between" style={{ width: '100%' }}>
        <Heading size="xs" className={styles.label}>
          {label}
        </Heading>
        <Heading size="xs" className={styles.label}>
          {Math.round(currentValue)}%
        </Heading>
      </Flex>
      <Progress
        value={currentValue}
        variant="classic"
        size="small"
        style={progressStyle}
        className={clsx(styles.detailedMetricsProgress)}
      />
    </Flex>
  )
}

export const DetailedMetrics = ({
  responseRate,
  responseSpeed,
  consistency,
}: {
  responseRate: CommentsScoreMetric
  responseSpeed: CommentsScoreMetric
  consistency: CommentsScoreMetric
}): JSX.Element => {
  return (
    <Flex direction="column" gap="md" style={{ width: '100%' }}>
      <Metric label="Response rate" value={responseRate.percentage} />
      <Metric label="Response speed" value={responseSpeed.percentage} />
      <Metric label="Consistency" value={consistency.percentage} />
    </Flex>
  )
}
